import React, { useMemo, useState } from "react";
import { Users, MessagesSquare } from "lucide-react";
import AdminLogin from "../components/admin/AdminLogin";
import AdminHeader from "../components/admin/AdminHeader";
import AdminStats from "../components/admin/AdminStats";
import AdminFilters from "../components/admin/AdminFilters";
import AdminTable from "../components/admin/AdminTable";
import AdminCommunityPosts from "../components/admin/AdminCommunityPosts";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { useSignupsData } from "../hooks/useSignupsData";
import { downloadCsv } from "../lib/csv";
import { toast } from "sonner";

const CSV_HEADERS = ["id", "first_name", "email", "source", "created_at"];

const filterRows = (items, query, sourceFilter) => {
  const q = query.trim().toLowerCase();
  return items.filter((it) => {
    const source = it.source || "hero";
    if (sourceFilter !== "all" && source !== sourceFilter) {
      return false;
    }
    if (!q) return true;
    const name = (it.first_name || "").toLowerCase();
    const email = (it.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });
};

const countBySource = (items) => {
  const counts = { hero: 0, final_cta: 0 };
  for (const it of items) {
    const source = it.source || "hero";
    if (counts[source] !== undefined) {
      counts[source] += 1;
    }
  }
  return counts;
};

const TABS = [
  { id: "signups", label: "Signups", icon: Users },
  { id: "community", label: "Community", icon: MessagesSquare },
];

const SignupsPanel = ({ adminKey, signOut }) => {
  const { items, stats, loading, reload } = useSignupsData(adminKey, signOut);
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  const filtered = useMemo(
    () => filterRows(items, query, sourceFilter),
    [items, query, sourceFilter]
  );
  const sourceCounts = useMemo(() => countBySource(items), [items]);

  const handleExportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export.");
      return;
    }
    const filename = `never-alone-sg-signups-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    const ok = downloadCsv(filename, CSV_HEADERS, filtered);
    if (!ok) {
      toast.error("Could not generate CSV.");
    }
  };

  return (
    <>
      <AdminStats stats={stats} sourceCounts={sourceCounts} />
      <AdminFilters
        query={query}
        onQueryChange={setQuery}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        onExportCsv={handleExportCsv}
      />
      <AdminTable rows={filtered} totalRows={items.length} loading={loading} />
      <div className="mt-3 flex justify-end">
        <button
          onClick={reload}
          className="text-sm text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
        >
          Refresh signups
        </button>
      </div>
    </>
  );
};

const Admin = () => {
  const { adminKey, isAuthenticated, verifying, signIn, signOut } = useAdminAuth();
  const [tab, setTab] = useState("signups");

  if (!isAuthenticated) {
    return <AdminLogin onSubmit={signIn} loading={verifying} />;
  }

  return (
    <div className="min-h-screen bg-[#f6f9fc]">
      <AdminHeader onRefresh={() => window.location.reload()} onSignOut={signOut} refreshing={false} />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center gap-2 border-b border-[#e6eef5]">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`ht-btn inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${
                  active
                    ? "border-[#3d6486] text-[#2c3e50]"
                    : "border-transparent text-[#5a6b7d] hover:text-[#2c3e50]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {tab === "signups" && (
            <SignupsPanel adminKey={adminKey} signOut={signOut} />
          )}
          {tab === "community" && (
            <AdminCommunityPosts adminKey={adminKey} onUnauthorized={signOut} />
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
